import axios from 'axios';
import { getCookie, isJwtExpired } from '../util_functions';

// Fired on window when a request carrying an expired cms_token_3 is answered 401: the session is over, and the
// shell logs the tab out instead of every poll and beacon retrying forever. Announced once per token, so a tab
// that logs back in without a reload is covered again at its next expiry.
export const SESSION_EXPIRED_EVENT = 'myt:session-expired';
let announcedForToken = null;

const onAuthFailure = (error, usedToken) => {
  if (!usedToken || error?.response?.status !== 401) return;
  const token = getCookie('cms_token_3');
  // No cookie any more: the tab is already logged out. A 401 on a live token is a permission problem, not
  // an expired session; leave those alone.
  if (!token || !isJwtExpired(token) || announcedForToken === token) return;
  announcedForToken = token;
  try {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  } catch (e) {
    // no window (tests); nothing to tell
  }
};

export const resetSessionExpiredForTests = () => {
  announcedForToken = null;
};

// Every request needs a ceiling: axios defaults to no timeout, so a stalled
// socket leaves the promise pending forever and callers that optimistically
// updated their UI never learn the write failed.
export const DEFAULT_TIMEOUT_MS = 30000;
// File uploads legitimately run longer than a normal API call, so they get a
// wider ceiling instead of being excluded from the timeout.
export const UPLOAD_TIMEOUT_MS = 120000;

const resolveTimeout = (type, timeout) => {
  if (typeof timeout === 'number') return timeout;
  return type === 'formdata' ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
};

export const post_api = (
  url,
  payload,
  token = false,
  type = 'none',
  timeout = null,
  responseType = null
) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${getCookie('cms_token_3')}`;
  }
  if (type === 'formdata') {
    headers['content-type'] = 'multipart/form-data';
  }
  return new Promise((resolve, reject) =>
    axios({
      method: 'post',
      url,
      headers,
      data: payload,
      timeout: resolveTimeout(type, timeout),
      responseType: responseType || undefined
    })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        console.log(error);
        onAuthFailure(error, token);
        reject(error);
      })
  );
};

export const put_api = (url, payload, token = false, type = 'none', timeout = null) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${getCookie('cms_token_3')}`;
  }
  if (type === 'formdata') {
    headers['content-type'] = 'multipart/form-data';
  }
  return new Promise((resolve, reject) =>
    axios({
      method: 'put',
      url,
      headers,
      data: payload,
      timeout: resolveTimeout(type, timeout)
    })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        console.log(error);
        onAuthFailure(error, token);
        reject(error);
      })
  );
};

export const api_with_method = (
  api_method,
  url,
  payload,
  token = false,
  type = 'none',
  timeout = null
) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${getCookie('cms_token_3')}`;
  }
  if (type === 'formdata') {
    headers['content-type'] = 'multipart/form-data';
  }
  return new Promise((resolve, reject) =>
    axios({
      method: api_method,
      url,
      headers,
      data: payload,
      timeout: resolveTimeout(type, timeout)
    })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        console.log(error);
        onAuthFailure(error, token);
        reject(error);
      })
  );
};

// responseType 'blob' for a file download (a PDF or a bank file kept byte for byte).
export const get_api = (url, token = false, timeout = null, responseType = null) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${getCookie('cms_token_3')}`;
  }
  return axios({
    method: 'get',
    url,
    headers,
    timeout: resolveTimeout('none', timeout),
    responseType: responseType || undefined
  })
    .then((response) => Promise.resolve(response))
    .catch((error) => {
      onAuthFailure(error, token);
      return Promise.reject(error);
    });
};

export const delete_api = (url, token = false, timeout = null) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${getCookie('cms_token_3')}`;
  }
  return axios({
    method: 'delete',
    url,
    headers,
    timeout: resolveTimeout('none', timeout)
  })
    .then((response) => Promise.resolve(response))
    .catch((error) => {
      onAuthFailure(error, token);
      return Promise.reject(error);
    });
};
